import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { profilePath } from '../../utils/profilePath';

/**
 * Macht Name und/oder Avatar eines Users anklickbar → öffentliches Profil.
 *
 * Regel (Marcel, 2026-09-03): ÜBERALL, wo ein Benutzer-/Anzeigename oder ein
 * Profilbild zu sehen ist, muss man aufs Profil kommen. Das Ziel wird NIE aus
 * dem angezeigten Namen gebaut (das ist seit den Live-Anzeigenamen der
 * Anzeigename), sondern aus `username` (echter Benutzername) oder `userId`.
 *
 * - Ohne Ziel (weder Username noch ID) werden die Kinder unverändert in einem
 *   `<span>` gerendert – nie ein kaputter Link.
 * - `nested`: für Stellen, die bereits in einem `<a>`/`<button>` liegen
 *   (Topic-Karte, Post-Karte). Dann wird kein verschachteltes `<a>` erzeugt,
 *   sondern ein `<span role="link">`, das den Klick abfängt und navigiert.
 * - Klicks werden standardmäßig nicht an umschließende Karten durchgereicht.
 * - Keine eigene Textfarbe: Light-/Dark-Mode-Farben kommen vom Aufrufer,
 *   der Link ergänzt nur Hover-Unterstreichung + Fokusring.
 */
export interface ProfileLinkProps {
  userId?: string | null;
  /** Echter Benutzername – NICHT den Anzeigenamen übergeben. */
  username?: string | null;
  /** Für den Tooltip ("Profil von X ansehen"). */
  displayName?: string | null;
  className?: string;
  /** In einem bestehenden Link/Button? Dann span + navigate statt <a>. */
  nested?: boolean;
  stopPropagation?: boolean;
  /** Link auch bei fehlendem Ziel als span mit dieser Klasse rendern. */
  fallbackClassName?: string;
  title?: string;
  children: React.ReactNode;
}

const BASE_CLS =
  'cursor-pointer transition-colors hover:underline decoration-1 underline-offset-2 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60 rounded-md';

const ProfileLink: React.FC<ProfileLinkProps> = ({
  userId,
  username,
  displayName,
  className = '',
  nested = false,
  stopPropagation = true,
  fallbackClassName,
  title,
  children,
}) => {
  const navigate = useNavigate();
  const to = profilePath({ username, userId });

  if (!to) {
    return <span className={fallbackClassName ?? className}>{children}</span>;
  }

  const label = title ?? (displayName ? `Profil von ${displayName} ansehen` : 'Profil ansehen');
  const cls = `${BASE_CLS} ${className}`.trim();

  if (nested) {
    const go = (event: React.SyntheticEvent) => {
      event.preventDefault();
      if (stopPropagation) event.stopPropagation();
      navigate(to);
    };
    return (
      <span
        role="link"
        tabIndex={0}
        title={label}
        className={cls}
        onClick={go}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') go(event);
        }}
      >
        {children}
      </span>
    );
  }

  return (
    <RouterLink
      to={to}
      title={label}
      className={cls}
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation();
      }}
    >
      {children}
    </RouterLink>
  );
};

export default ProfileLink;
